/**
 * Intent Classifier for AI Chat
 *
 * Extracts intent detection logic from chat components for better maintainability.
 * Uses keyword matching with configurable patterns.
 */

// ============================================================================
// Types
// ============================================================================

export type LayoutIntent =
  | "suggest_layout"
  | "work_triangle"
  | "kashrut"
  | "unknown";

export type RenderIntent =
  | "daylight"
  | "evening"
  | "studio"
  | "custom";

export type TipsIntent =
  | "storage"
  | "materials"
  | "accessibility"
  | "kashrut"
  | "custom";

export interface IntentResult<T> {
  intent: T;
  confidence: "high" | "medium" | "low";
  matchedKeywords: string[];
}

// ============================================================================
// Keyword Patterns
// ============================================================================

const LAYOUT_PATTERNS: Record<LayoutIntent, string[]> = {
  suggest_layout: ["suggest", "layout", "optimize", "arrange", "recommend"],
  work_triangle: ["triangle", "analyze", "efficiency", "workflow", "ergonomic"],
  kashrut: ["kashrut", "kosher", "meat", "dairy", "separation"],
  unknown: [],
};

const RENDER_PATTERNS: Record<RenderIntent, string[]> = {
  daylight: ["day", "daylight", "natural", "sunlight", "morning", "bright"],
  evening: ["evening", "warm", "sunset", "cozy", "ambient"],
  studio: ["studio", "professional", "commercial", "product", "showcase"],
  custom: [],
};

const TIPS_PATTERNS: Record<TipsIntent, string[]> = {
  storage: ["storage", "store", "organize", "cabinet", "drawer", "shelf"],
  materials: ["material", "surface", "countertop", "finish", "durability"],
  accessibility: ["access", "ergonomic", "height", "wheelchair", "ada"],
  kashrut: ["kashrut", "kosher", "meat", "dairy", "separation"],
  custom: [],
};

// ============================================================================
// Classifier Functions
// ============================================================================

function matchPatterns<T extends string>(
  input: string,
  patterns: Record<T, string[]>,
  defaultIntent: T
): IntentResult<T> {
  const lowerInput = input.toLowerCase();
  const matches: { intent: T; keywords: string[] }[] = [];

  for (const [intent, keywords] of Object.entries(patterns) as [T, string[]][]) {
    const matchedKeywords = keywords.filter((kw) => lowerInput.includes(kw));
    if (matchedKeywords.length > 0) {
      matches.push({ intent, keywords: matchedKeywords });
    }
  }

  if (matches.length === 0) {
    return {
      intent: defaultIntent,
      confidence: "low",
      matchedKeywords: [],
    };
  }

  // Sort by number of matched keywords (more matches = higher confidence)
  matches.sort((a, b) => b.keywords.length - a.keywords.length);
  const best = matches[0];

  return {
    intent: best.intent,
    confidence: best.keywords.length >= 2 ? "high" : "medium",
    matchedKeywords: best.keywords,
  };
}

/**
 * Classify layout-related user input
 */
export function classifyLayoutIntent(input: string): IntentResult<LayoutIntent> {
  return matchPatterns(input, LAYOUT_PATTERNS, "unknown");
}

/**
 * Classify render-related user input
 */
export function classifyRenderIntent(input: string): IntentResult<RenderIntent> {
  return matchPatterns(input, RENDER_PATTERNS, "custom");
}

/**
 * Classify tips-related user input
 */
export function classifyTipsIntent(input: string): IntentResult<TipsIntent> {
  return matchPatterns(input, TIPS_PATTERNS, "custom");
}

/**
 * Get a human-readable render style from intent
 */
export function getRenderStyleFromIntent(intent: RenderIntent): string {
  const styles: Record<RenderIntent, string> = {
    daylight: "natural daylight",
    evening: "warm evening",
    studio: "professional studio",
    custom: "custom style",
  };
  return styles[intent];
}

/**
 * Check if input is a layout-related query
 */
export function isLayoutQuery(input: string): boolean {
  const result = classifyLayoutIntent(input);
  return result.intent !== "unknown" || result.confidence !== "low";
}
